import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import { ModerationReportModal } from './ModerationReportModal';
import { useModerationReportingEnabled } from './useModerationReportingEnabled';
import { ComplexTooltip } from '../presentational/tooltip';

export interface IModerationReportButtonProps {
  contextType: ModerationReportContextType;
  entityId: number;
  entityName?: string;
  sourceUrl?: string;
  variant?: 'icon' | 'text' | 'icon-text';
  showOnHover?: boolean;
  mobileOptional?: boolean;
  linkStyle?: boolean; // Makes the button look like a link without button styling
  className?: string;
}

export function ModerationReportButton(props: IModerationReportButtonProps) {
  const {
    contextType,
    entityId,
    entityName,
    sourceUrl,
    variant = 'icon',
    showOnHover = false,
    mobileOptional = false,
    linkStyle = false,
    className
  } = props;

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canReport = useModerationReportingEnabled();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleClose = () => setIsModalOpen(false);

  if (!canReport) {
    return null;
  }

  const buttonClasses = cx(
    'moderation-report-button',
    {
      'moderation-report-button--show-on-hover': showOnHover,
      'moderation-report-button--mobile-optional': mobileOptional,
      'moderation-report-button--icon-only': variant === 'icon',
      'moderation-report-button--text-only': variant === 'text',
      'moderation-report-button--icon-text': variant === 'icon-text',
      'moderation-report-button--link-style': linkStyle
    },
    className
  );

  return (
    <>
      <ComplexTooltip tooltip={t('moderation:button.tooltip')} placement="top">
        <button
          type="button"
          className={buttonClasses}
          onClick={handleClick}
          aria-label={t('moderation:button.tooltip')}
        >
          {(variant === 'icon' || variant === 'icon-text') && <i className="fa fa-flag" />}
          {(variant === 'text' || variant === 'icon-text') && (
            <span className="moderation-report-button__text">{t('moderation:button.report')}</span>
          )}
        </button>
      </ComplexTooltip>

      {isModalOpen && (
        <ModerationReportModal
          isOpen={isModalOpen}
          onClose={handleClose}
          contextType={contextType}
          entityId={entityId}
          entityName={entityName}
          sourceUrl={sourceUrl}
        />
      )}
    </>
  );
}
